import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
    StatusComponent,
    Incident,
    ScheduledMaintenance,
    StatusSubscriber,
    ComponentStatus,
    IncidentStatus,
    IncidentImpact,
    ComponentCategory,
    subscribeToStatusComponents,
    subscribeToIncidents,
    getScheduledMaintenance,
    getActiveSubscribers,
    updateComponentStatus,
    createComponent,
    deleteComponent,
    createIncident,
    addIncidentUpdate,
    createMaintenance,
    startMaintenance,
    completeMaintenance,
    getStatusColor,
    getStatusLabel,
    seedStatusComponents,
    cleanupAndReseedComponents
} from '../../services/statusPageService';

// ============================================================================
// TYPES
// ============================================================================

type ActiveTab = 'overview' | 'components' | 'incidents' | 'maintenance' | 'subscribers';

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface TabButtonProps {
    active: boolean;
    onClick: () => void;
    icon: string;
    label: string;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, icon, label }) => (
    <button
        onClick={onClick}
        className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${active
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
    >
        <span>{icon}</span>
        <span>{label}</span>
    </button>
);

interface StatusBadgeProps {
    status: ComponentStatus;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => (
    <span
        className="px-2 py-1 rounded-full text-xs font-medium"
        style={{
            backgroundColor: `${getStatusColor(status)}20`,
            color: getStatusColor(status)
        }}
    >
        {getStatusLabel(status)}
    </span>
);

// ============================================================================
// OVERVIEW TAB
// ============================================================================

interface OverviewTabProps {
    components: StatusComponent[];
    incidents: Incident[];
    maintenance: ScheduledMaintenance[];
    subscribers: StatusSubscriber[];
}

const OverviewTab: React.FC<OverviewTabProps> = ({ components, incidents, maintenance, subscribers }) => {
    const operationalCount = components.filter(c => c.status === 'operational').length;
    const issueCount = components.filter(c => c.status !== 'operational').length;
    const activeIncidents = incidents.filter(i => i.status !== 'resolved').length;

    return (
        <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-3xl font-bold text-green-600">{operationalCount}</div>
                    <div className="text-sm text-gray-600">Operational Components</div>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-3xl font-bold text-red-600">{issueCount}</div>
                    <div className="text-sm text-gray-600">Components with Issues</div>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-3xl font-bold text-orange-600">{activeIncidents}</div>
                    <div className="text-sm text-gray-600">Active Incidents</div>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-3xl font-bold text-blue-600">{subscribers.length}</div>
                    <div className="text-sm text-gray-600">Subscribers</div>
                </div>
            </div>

            {/* Quick Status Overview */}
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">System Status Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {components.slice(0, 9).map(component => (
                        <div key={component.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium">{component.name}</span>
                            <StatusBadge status={component.status} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// COMPONENTS TAB
// ============================================================================

interface ComponentsTabProps {
    components: StatusComponent[];
    onUpdateStatus: (componentId: string, status: ComponentStatus) => Promise<void>;
    onSeedComponents: () => Promise<void>;
    onCleanupComponents: () => Promise<void>;
}

const ComponentsTab: React.FC<ComponentsTabProps> = ({ components, onUpdateStatus, onSeedComponents, onCleanupComponents }) => {
    const [loading, setLoading] = useState<string | null>(null);
    const [cleanupLoading, setCleanupLoading] = useState(false);

    const statusOptions: ComponentStatus[] = ['operational', 'degraded', 'partial_outage', 'major_outage', 'maintenance'];

    const handleStatusChange = async (componentId: string, newStatus: ComponentStatus) => {
        setLoading(componentId);
        await onUpdateStatus(componentId, newStatus);
        setLoading(null);
    };

    const groupedComponents = components.reduce((acc, component) => {
        if (!acc[component.category]) {
            acc[component.category] = [];
        }
        acc[component.category].push(component);
        return acc;
    }, {} as Record<ComponentCategory, StatusComponent[]>);

    const categoryLabels: Record<ComponentCategory, { title: string; icon: string }> = {
        channels: { title: 'Channels', icon: '📬' },
        platforms: { title: 'Platforms', icon: '🔌' },
        product_areas: { title: 'Product Areas', icon: '🛠️' }
    };

    if (components.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow p-8 text-center">
                <p className="text-gray-600 mb-4">No status components found. Seed initial components to get started.</p>
                <button
                    onClick={onSeedComponents}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    Seed Status Components
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Cleanup button for duplicates */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
                <div>
                    <p className="font-medium text-yellow-800">Have duplicate components?</p>
                    <p className="text-sm text-yellow-600">This will delete all components and re-seed fresh ones.</p>
                </div>
                <button
                    onClick={async () => {
                        setCleanupLoading(true);
                        await onCleanupComponents();
                        setCleanupLoading(false);
                    }}
                    disabled={cleanupLoading}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                >
                    {cleanupLoading ? 'Cleaning...' : 'Cleanup & Reseed'}
                </button>
            </div>

            {Object.entries(groupedComponents).map(([category, comps]) => (
                <div key={category} className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold flex items-center space-x-2">
                            <span>{categoryLabels[category as ComponentCategory]?.icon}</span>
                            <span>{categoryLabels[category as ComponentCategory]?.title}</span>
                        </h3>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {comps.map(component => (
                            <div key={component.id} className="px-6 py-4 flex items-center justify-between">
                                <div>
                                    <div className="font-medium">{component.name}</div>
                                    <div className="text-sm text-gray-500">{component.description}</div>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <select
                                        value={component.status}
                                        onChange={(e) => handleStatusChange(component.id, e.target.value as ComponentStatus)}
                                        disabled={loading === component.id}
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        {statusOptions.map(status => (
                                            <option key={status} value={status}>
                                                {getStatusLabel(status)}
                                            </option>
                                        ))}
                                    </select>
                                    {loading === component.id && (
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

// ============================================================================
// INCIDENTS TAB
// ============================================================================

interface IncidentsTabProps {
    incidents: Incident[];
    components: StatusComponent[];
    onCreateIncident: (title: string, impact: IncidentImpact, components: string[], message: string) => Promise<void>;
    onAddUpdate: (incidentId: string, status: IncidentStatus, message: string) => Promise<void>;
}

const IncidentsTab: React.FC<IncidentsTabProps> = ({ incidents, components, onCreateIncident, onAddUpdate }) => {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newIncident, setNewIncident] = useState({
        title: '',
        impact: 'minor' as IncidentImpact,
        components: [] as string[],
        message: ''
    });
    const [updateForm, setUpdateForm] = useState<{
        incidentId: string;
        status: IncidentStatus;
        message: string;
    } | null>(null);
    const [loading, setLoading] = useState(false);

    const handleCreateIncident = async () => {
        if (!newIncident.title || !newIncident.message) return;
        setLoading(true);
        try {
            await onCreateIncident(newIncident.title, newIncident.impact, newIncident.components, newIncident.message);
            setNewIncident({ title: '', impact: 'minor', components: [], message: '' });
            setShowCreateForm(false);
        } finally {
            setLoading(false);
        }
    };

    const handleAddUpdate = async () => {
        if (!updateForm || !updateForm.message) return;
        setLoading(true);
        try {
            await onAddUpdate(updateForm.incidentId, updateForm.status, updateForm.message);
            setUpdateForm(null);
        } finally {
            setLoading(false);
        }
    };

    const activeIncidents = incidents.filter(i => i.status !== 'resolved');
    const pastIncidents = incidents.filter(i => i.status === 'resolved');

    return (
        <div className="space-y-6">
            {/* Create Incident Button */}
            <div className="flex justify-end">
                <button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                    {showCreateForm ? 'Cancel' : '+ Report Incident'}
                </button>
            </div>

            {/* Create Incident Form */}
            {showCreateForm && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4">Report New Incident</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                                type="text"
                                value={newIncident.title}
                                onChange={(e) => setNewIncident({ ...newIncident, title: e.target.value })}
                                placeholder="Brief incident title"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Impact</label>
                            <select
                                value={newIncident.impact}
                                onChange={(e) => setNewIncident({ ...newIncident, impact: e.target.value as IncidentImpact })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            >
                                <option value="none">None</option>
                                <option value="minor">Minor</option>
                                <option value="major">Major</option>
                                <option value="critical">Critical</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Affected Components</label>
                            <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                                {components.map(component => (
                                    <label key={component.id} className="flex items-center space-x-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={newIncident.components.includes(component.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setNewIncident({ ...newIncident, components: [...newIncident.components, component.id] });
                                                } else {
                                                    setNewIncident({ ...newIncident, components: newIncident.components.filter(id => id !== component.id) });
                                                }
                                            }}
                                        />
                                        <span>{component.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Initial Message</label>
                            <textarea
                                value={newIncident.message}
                                onChange={(e) => setNewIncident({ ...newIncident, message: e.target.value })}
                                placeholder="Describe the incident..."
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                        </div>
                        <button
                            onClick={handleCreateIncident}
                            disabled={loading || !newIncident.title || !newIncident.message}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                        >
                            {loading ? 'Creating...' : 'Create Incident'}
                        </button>
                    </div>
                </div>
            )}

            {/* Active Incidents */}
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold">Active Incidents ({activeIncidents.length})</h3>
                </div>
                {activeIncidents.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">No active incidents</div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {activeIncidents.map(incident => (
                            <div key={incident.id} className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <h4 className="font-semibold text-lg">{incident.title}</h4>
                                        <span className={`text-sm ${incident.status === 'investigating' ? 'text-red-600' :
                                            incident.status === 'identified' ? 'text-orange-600' :
                                                'text-blue-600'
                                            }`}>
                                            Status: {incident.status}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => setUpdateForm({
                                            incidentId: incident.id,
                                            status: incident.status,
                                            message: ''
                                        })}
                                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                                    >
                                        Add Update
                                    </button>
                                </div>

                                {/* Update Form for this incident */}
                                {updateForm?.incidentId === incident.id && (
                                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-sm font-medium mb-1">New Status</label>
                                                <select
                                                    value={updateForm.status}
                                                    onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value as IncidentStatus })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                >
                                                    <option value="investigating">Investigating</option>
                                                    <option value="identified">Identified</option>
                                                    <option value="monitoring">Monitoring</option>
                                                    <option value="resolved">Resolved</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Update Message</label>
                                                <textarea
                                                    value={updateForm.message}
                                                    onChange={(e) => setUpdateForm({ ...updateForm, message: e.target.value })}
                                                    rows={2}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                />
                                            </div>
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={handleAddUpdate}
                                                    disabled={loading || !updateForm.message}
                                                    className="px-3 py-1 bg-blue-600 text-white rounded-lg disabled:opacity-50"
                                                >
                                                    {loading ? 'Posting...' : 'Post Update'}
                                                </button>
                                                <button
                                                    onClick={() => setUpdateForm(null)}
                                                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Updates Timeline */}
                                <div className="space-y-2">
                                    {incident.updates.slice().reverse().map(update => (
                                        <div key={update.id} className="text-sm border-l-2 border-gray-200 pl-3">
                                            <div className="font-medium capitalize">{update.status}</div>
                                            <div className="text-gray-600">{update.message}</div>
                                            <div className="text-xs text-gray-400">
                                                {update.createdAt.toDate().toLocaleString()}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Past Incidents */}
            {pastIncidents.length > 0 && (
                <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold">Past Incidents</h3>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {pastIncidents.slice(0, 5).map(incident => (
                            <div key={incident.id} className="px-6 py-4 flex items-center justify-between">
                                <div>
                                    <span className="font-medium">{incident.title}</span>
                                    <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Resolved</span>
                                </div>
                                <span className="text-sm text-gray-500">
                                    {incident.resolvedAt?.toDate().toLocaleDateString()}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// ============================================================================
// MAINTENANCE TAB
// ============================================================================

interface MaintenanceTabProps {
    maintenance: ScheduledMaintenance[];
    components: StatusComponent[];
    onCreateMaintenance: (title: string, description: string, components: string[], start: Date, end: Date) => Promise<void>;
    onStartMaintenance: (id: string) => Promise<void>;
    onCompleteMaintenance: (id: string) => Promise<void>;
}

const MaintenanceTab: React.FC<MaintenanceTabProps> = ({
    maintenance,
    components,
    onCreateMaintenance,
    onStartMaintenance,
    onCompleteMaintenance
}) => {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        title: '',
        description: '',
        components: [] as string[],
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: ''
    });
    const [loading, setLoading] = useState(false);

    const handleCreate = async () => {
        if (!form.title || !form.startDate || !form.endDate) return;
        setLoading(true);
        try {
            const start = new Date(`${form.startDate}T${form.startTime || '00:00'}`);
            const end = new Date(`${form.endDate}T${form.endTime || '23:59'}`);
            await onCreateMaintenance(form.title, form.description, form.components, start, end);
            setForm({ title: '', description: '', components: [], startDate: '', startTime: '', endDate: '', endTime: '' });
            setShowForm(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    {showForm ? 'Cancel' : '+ Schedule Maintenance'}
                </button>
            </div>

            {showForm && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4">Schedule Maintenance</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                                type="text"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                <input
                                    type="date"
                                    value={form.startDate}
                                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                                <input
                                    type="time"
                                    value={form.startTime}
                                    onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                <input
                                    type="date"
                                    value={form.endDate}
                                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                                <input
                                    type="time"
                                    value={form.endTime}
                                    onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                        </div>
                        <button
                            onClick={handleCreate}
                            disabled={loading || !form.title}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
                        >
                            {loading ? 'Creating...' : 'Schedule Maintenance'}
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold">Scheduled Maintenance</h3>
                </div>
                {maintenance.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">No scheduled maintenance</div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {maintenance.map(m => (
                            <div key={m.id} className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h4 className="font-semibold">{m.title}</h4>
                                        <p className="text-sm text-gray-600">{m.description}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {m.scheduledStartTime.toDate().toLocaleString()} - {m.scheduledEndTime.toDate().toLocaleString()}
                                        </p>
                                    </div>
                                    <div className="flex space-x-2">
                                        {m.status === 'scheduled' && (
                                            <button
                                                onClick={() => onStartMaintenance(m.id)}
                                                className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg"
                                            >
                                                Start
                                            </button>
                                        )}
                                        {m.status === 'in_progress' && (
                                            <button
                                                onClick={() => onCompleteMaintenance(m.id)}
                                                className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg"
                                            >
                                                Complete
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// SUBSCRIBERS TAB
// ============================================================================

interface SubscribersTabProps {
    subscribers: StatusSubscriber[];
}

const SubscribersTab: React.FC<SubscribersTabProps> = ({ subscribers }) => {
    return (
        <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Status Subscribers ({subscribers.length})</h3>
            </div>
            {subscribers.length === 0 ? (
                <div className="p-6 text-center text-gray-500">No subscribers yet</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscribed</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preferences</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {subscribers.map(sub => (
                                <tr key={sub.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{sub.email}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {sub.createdAt?.toDate().toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <div className="flex space-x-2">
                                            {sub.notifyOnIncidents && <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">Incidents</span>}
                                            {sub.notifyOnMaintenance && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Maintenance</span>}
                                            {sub.notifyOnResolution && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Resolutions</span>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AdminStatusPageManager: React.FC = () => {
    const { currentUser } = useAuth();
    const { showSuccess, showError } = useNotification();

    const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
    const [components, setComponents] = useState<StatusComponent[]>([]);
    const [incidents, setIncidents] = useState<Incident[]>([]);
    const [maintenance, setMaintenance] = useState<ScheduledMaintenance[]>([]);
    const [subscribers, setSubscribers] = useState<StatusSubscriber[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Subscribe to real-time updates
        const unsubComponents = subscribeToStatusComponents(setComponents);
        const unsubIncidents = subscribeToIncidents(setIncidents);

        // Load maintenance and subscribers
        const loadData = async () => {
            try {
                const [maintenanceData, subscriberData] = await Promise.all([
                    getScheduledMaintenance(),
                    getActiveSubscribers()
                ]);
                setMaintenance(maintenanceData);
                setSubscribers(subscriberData);
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();

        return () => {
            unsubComponents();
            unsubIncidents();
        };
    }, []);

    // Handlers
    const handleUpdateStatus = async (componentId: string, status: ComponentStatus) => {
        if (!currentUser) return;
        const success = await updateComponentStatus(componentId, status, currentUser.uid);
        if (success) {
            showSuccess('Component status updated');
        } else {
            showError('Failed to update status');
        }
    };

    const handleSeedComponents = async () => {
        if (!currentUser) return;
        const success = await seedStatusComponents(currentUser.uid);
        if (success) {
            showSuccess('Status components seeded successfully');
        } else {
            showError('Failed to seed components');
        }
    };

    const handleCleanupComponents = async () => {
        if (!currentUser) return;
        if (!window.confirm('This will delete ALL existing components and re-seed from scratch. Are you sure?')) {
            return;
        }
        const success = await cleanupAndReseedComponents(currentUser.uid);
        if (success) {
            showSuccess('Components cleaned up and re-seeded successfully');
        } else {
            showError('Failed to cleanup components');
        }
    };

    const handleCreateIncident = async (title: string, impact: IncidentImpact, comps: string[], message: string) => {
        if (!currentUser) return;
        const incidentId = await createIncident(title, impact, comps, message, currentUser.uid);
        if (incidentId) {
            showSuccess('Incident created');
        } else {
            showError('Failed to create incident');
        }
    };

    const handleAddUpdate = async (incidentId: string, status: IncidentStatus, message: string) => {
        if (!currentUser) return;
        const success = await addIncidentUpdate(incidentId, status, message, currentUser.uid);
        if (success) {
            showSuccess('Incident updated');
        } else {
            showError('Failed to update incident');
        }
    };

    const handleCreateMaintenance = async (title: string, description: string, comps: string[], start: Date, end: Date) => {
        if (!currentUser) return;
        const maintenanceId = await createMaintenance(title, description, comps, start, end, currentUser.uid);
        if (maintenanceId) {
            showSuccess('Maintenance scheduled');
            const data = await getScheduledMaintenance();
            setMaintenance(data);
        } else {
            showError('Failed to schedule maintenance');
        }
    };

    const handleStartMaintenance = async (id: string) => {
        if (!currentUser) return;
        const success = await startMaintenance(id, currentUser.uid);
        if (success) {
            showSuccess('Maintenance started');
            const data = await getScheduledMaintenance();
            setMaintenance(data);
        }
    };

    const handleCompleteMaintenance = async (id: string) => {
        if (!currentUser) return;
        const success = await completeMaintenance(id, currentUser.uid);
        if (success) {
            showSuccess('Maintenance completed');
            const data = await getScheduledMaintenance();
            setMaintenance(data);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Status Page Management</h1>
                    <p className="text-gray-600">Manage system status, incidents, and maintenance</p>
                </div>
                <a
                    href="/status"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                    View Public Status Page →
                </a>
            </div>

            {/* Tabs */}
            <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
                <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} icon="📊" label="Overview" />
                <TabButton active={activeTab === 'components'} onClick={() => setActiveTab('components')} icon="🔧" label="Components" />
                <TabButton active={activeTab === 'incidents'} onClick={() => setActiveTab('incidents')} icon="⚠️" label="Incidents" />
                <TabButton active={activeTab === 'maintenance'} onClick={() => setActiveTab('maintenance')} icon="🔧" label="Maintenance" />
                <TabButton active={activeTab === 'subscribers'} onClick={() => setActiveTab('subscribers')} icon="📧" label="Subscribers" />
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <OverviewTab
                    components={components}
                    incidents={incidents}
                    maintenance={maintenance}
                    subscribers={subscribers}
                />
            )}

            {activeTab === 'components' && (
                <ComponentsTab
                    components={components}
                    onUpdateStatus={handleUpdateStatus}
                    onSeedComponents={handleSeedComponents}
                    onCleanupComponents={handleCleanupComponents}
                />
            )}

            {activeTab === 'incidents' && (
                <IncidentsTab
                    incidents={incidents}
                    components={components}
                    onCreateIncident={handleCreateIncident}
                    onAddUpdate={handleAddUpdate}
                />
            )}

            {activeTab === 'maintenance' && (
                <MaintenanceTab
                    maintenance={maintenance}
                    components={components}
                    onCreateMaintenance={handleCreateMaintenance}
                    onStartMaintenance={handleStartMaintenance}
                    onCompleteMaintenance={handleCompleteMaintenance}
                />
            )}

            {activeTab === 'subscribers' && (
                <SubscribersTab subscribers={subscribers} />
            )}
        </div>
    );
};

export default AdminStatusPageManager;
